import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { pageLoaded } from 'services/adobe-launch/adobe-launch.actions';
import { selectPreviousLocation } from 'services/adobe-launch/adobe-launch.selectors';
import {
  selectIsSessionValidated,
  selectSessionInfo,
} from 'services/session/session.selectors';
import { trackAnalyticsEvent } from 'utils/functions/adobe-launch';
import { useIsApp } from './useGetIsApp';

/* -------------------------------------------------------------------------------------------------
 * types
 * -----------------------------------------------------------------------------------------------*/
export type LoadedPageAnalyticsPayload = {
  pageName: string;
  trackOnce?: boolean;
  trackPreviousPage?: boolean;
} & AdditionalPayload;

type AdditionalPayload = { [key: string]: string | boolean | undefined };

/* -------------------------------------------------------------------------------------------------
 * helpers - page - load
 * -----------------------------------------------------------------------------------------------*/

function useLoadedPageAnalyticsCommon(
  pageName: string,
  previousPageName: string = '',
  additionalPayload: AdditionalPayload = {},
  trackOnce?: boolean,
) {
  const trackedOnce = useRef(false);
  const isSessionValidated = useSelector(selectIsSessionValidated);
  const { channel = 62 } = useSelector(selectSessionInfo);
  const isApp = useIsApp();

  useEffect(() => {
    if ((trackOnce && trackedOnce.current) || pageName === previousPageName)
      return;
    trackAnalyticsEvent({
      loaded_page: 'yes',
      page_name: pageName,
      conversion_pagename: pageName,
      previous_pagename: previousPageName,
      section: 'areaprivata',
      subsection1: '',
      subsection2: '',
      login_status: isSessionValidated ? 'logged' : 'not-logged',
      site_id: `${channel}`,
      webview: isApp ? 'yes' : 'no',
      url: window.location.origin + window.location.pathname,
      url_querystring: window.location.search,
      intcmp: '',
      operating_system: window.navigator.userAgent,
      ...additionalPayload,
    });

    trackedOnce.current = true;
  }, [
    additionalPayload,
    isSessionValidated,
    channel,
    pageName,
    previousPageName,
    trackOnce,
    isApp,
  ]);
}

export function useLoadedPageAnalytics({
  pageName,
  trackOnce,
  trackPreviousPage = true, // per le modali
  ...rest
}: LoadedPageAnalyticsPayload) {
  const dispatch = useDispatch();
  const previousPageName = useSelector(selectPreviousLocation);

  useEffect(() => {
    if (trackPreviousPage) dispatch(pageLoaded(pageName));
  }, [previousPageName, dispatch, trackPreviousPage, pageName]);

  useLoadedPageAnalyticsCommon(pageName, previousPageName, rest, trackOnce);
}
